import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "../lib/apiClient";
import { STATUSES } from "../lib/statusConfig";
import { divisionLabel } from "../lib/divisions";
import { BUCKETS, bucketLabel } from "../lib/buckets";
import { StatusBadge, DisciplineTag } from "../components/StatusBadge";
import { GrievanceForm } from "../components/GrievanceForm";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Search, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useColors } from "../context/ColorContext";
import { formatDate as fmtDate } from "../lib/dates";

const PAGE_SIZE = 20;


export default function Grievances({ lockedStatus = null, bucket = null, title = "Grievances", eyebrow = "Case Management" }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [discipline, setDiscipline] = useState("all");
  const [disciplines, setDisciplines] = useState([]);
  const [q, setQ] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(() => Math.max(1, Number(searchParams.get("page")) || 1));
  const [dateSort, setDateSort] = useState(null); // null | 'asc' | 'desc'
  // Local Grievances default to process order (Pending Submission → 1st Step → …); FWD/Terminations default to newest-added (created_at desc).
  const [statusSort, setStatusSort] = useState(() => (bucket === "local" ? "asc" : null)); // null | 'asc' | 'desc'
  const [selected, setSelected] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkDate, setBulkDate] = useState("");
  const [bulkBucket, setBulkBucket] = useState("");
  const [applying, setApplying] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [statusCounts, setStatusCounts] = useState([]);
  const [specialistFilter, setSpecialistFilter] = useState("all");
  const [specialistOptions, setSpecialistOptions] = useState([]);
  const [specialistSort, setSpecialistSort] = useState(null); // null | 'asc' | 'desc'
  const navigate = useNavigate();
  const { division = "ramp" } = useParams();
  const { can } = useAuth();
  const { statusStyle } = useColors();
  const canBulk = can("status");
  const isSpecialistBucket = bucket === "fwd_office" || bucket === "terminations";
  const showRep = bucket !== "terminations";

  useEffect(() => {
    api.get("/meta").then((r) => setDisciplines(r.data.disciplines || []));
    api.get("/options").then((r) => setSpecialistOptions(r.data.specialists || [])).catch(() => {});
  }, []);

  useEffect(() => { setStatus("all"); setDiscipline("all"); setQ(""); setSpecialistFilter("all"); }, [lockedStatus, bucket, division]);

  // Persist the current page in the URL so browser "back" returns to it (not page 1).
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (page > 1) sp.set("page", String(page)); else sp.delete("page");
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Reset to page 1 when filters/division/bucket change (but honor the URL page on first mount).
  const filterSig = `${lockedStatus}|${bucket}|${division}|${status}|${discipline}|${q}|${specialistFilter}`;
  const prevSig = React.useRef(filterSig);
  useEffect(() => {
    if (prevSig.current === filterSig) return;
    prevSig.current = filterSig;
    setPage(1);
  }, [filterSig]);

  const load = useCallback(() => {
    const effectiveStatus = lockedStatus || status;
    const params = { status: effectiveStatus, discipline, bucket: bucket || undefined, division, q };
    if (isSpecialistBucket && specialistFilter !== "all") params.specialist = specialistFilter;
    api.get("/grievances", { params })
      .then((r) => { setRows(r.data); setSelected(new Set()); });
    if (bucket === "local") {
      api.get("/stats", { params: { division, bucket: "local" } })
        .then((r) => setStatusCounts(r.data.by_status || []))
        .catch(() => {});
    }
  }, [status, discipline, q, lockedStatus, bucket, division, specialistFilter, isSpecialistBucket]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  // Clamp page if the current result set has fewer pages (e.g. after filtering). Skip while data is still loading.
  useEffect(() => {
    if (rows.length === 0) return;
    const tp = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
    if (page > tp) setPage(tp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const statusOrder = useMemo(() => {
    const m = {};
    STATUSES.forEach((s, i) => { m[s] = i; });
    return m;
  }, []);
  const sortedRows = useMemo(() => {
    if (statusSort) {
      const copy = [...rows];
      copy.sort((a, b) => {
        const oa = statusOrder[a.status] ?? 999;
        const ob = statusOrder[b.status] ?? 999;
        return statusSort === "asc" ? oa - ob : ob - oa;
      });
      return copy;
    }
    if (dateSort) {
      const copy = [...rows];
      copy.sort((a, b) => {
        const ta = a.date_due ? new Date(a.date_due).getTime() : null;
        const tb = b.date_due ? new Date(b.date_due).getTime() : null;
        if (ta === null && tb === null) return 0;
        if (ta === null) return 1; // nulls last
        if (tb === null) return -1;
        return dateSort === "asc" ? ta - tb : tb - ta;
      });
      return copy;
    }
    if (specialistSort) {
      const copy = [...rows];
      copy.sort((a, b) => {
        const sa = (a.specialist || "").toLowerCase();
        const sb = (b.specialist || "").toLowerCase();
        if (!sa && !sb) return 0;
        if (!sa) return 1; // unassigned last
        if (!sb) return -1;
        return specialistSort === "asc" ? sa.localeCompare(sb) : sb.localeCompare(sa);
      });
      return copy;
    }
    // Default (no manual sort) for FWD to Office / Terminations: newest grievance added first.
    if (bucket === "fwd_office" || bucket === "terminations") {
      const copy = [...rows];
      copy.sort((a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
      });
      return copy;
    }
    return rows;
  }, [rows, dateSort, statusSort, specialistSort, statusOrder, bucket]);
  const paged = useMemo(() => sortedRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [sortedRows, page]);

  const toggleDateSort = () => {
    setStatusSort(null);
    setSpecialistSort(null);
    setDateSort((s) => (s === "asc" ? "desc" : s === "desc" ? null : "asc"));
    setPage(1);
  };
  const toggleStatusSort = () => {
    setDateSort(null);
    setSpecialistSort(null);
    setStatusSort((s) => (s === "asc" ? "desc" : s === "desc" ? null : "asc"));
    setPage(1);
  };
  const toggleSpecialistSort = () => {
    setDateSort(null);
    setStatusSort(null);
    setSpecialistSort((s) => (s === "asc" ? "desc" : s === "desc" ? null : "asc"));
    setPage(1);
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const pageIds = paged.map((g) => g.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const togglePage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const clearSelection = () => { setSelected(new Set()); setBulkStatus(""); setBulkDate(""); setBulkBucket(""); };

  const applyBulk = async () => {
    if (!bulkStatus && !bulkDate && !bulkBucket) {
      toast.error("Choose a new status, due date or board to apply");
      return;
    }
    setApplying(true);
    try {
      const payload = { ids: Array.from(selected) };
      if (bulkStatus) payload.status = bulkStatus;
      if (bulkDate) payload.date_due = bulkDate;
      if (bulkBucket) payload.bucket = bulkBucket;
      const { data } = await api.post("/grievances/bulk-update", payload);
      toast.success(`Updated ${data.modified} grievance(s)`);
      clearSelection();
      load();
    } catch (e) {
      toast.error("Failed to update grievances");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">{divisionLabel(division)} · {eyebrow}</p>
          <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight">{title}</h1>
        </div>
        <Button data-testid="new-grievance-button" onClick={() => setFormOpen(true)}
          className={`rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 h-10 ${can("edit") ? "" : "hidden"}`}>
          <Plus className="w-4 h-4 mr-2" /> New Grievance
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="search-input" placeholder="Search name, subject, rep or case no…" value={q}
            onChange={(e) => setQ(e.target.value)} className="rounded-sm pl-9 h-10" />
        </div>
        {!lockedStatus && (
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="filter-status" className="rounded-sm w-[180px] h-10"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent className="bg-card">
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={discipline} onValueChange={setDiscipline}>
          <SelectTrigger data-testid="filter-discipline" className="rounded-sm w-[170px] h-10"><SelectValue placeholder="Discipline" /></SelectTrigger>
          <SelectContent className="bg-card">
            <SelectItem value="all">All Disciplines</SelectItem>
            {disciplines.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        {isSpecialistBucket && (
          <Select value={specialistFilter} onValueChange={setSpecialistFilter}>
            <SelectTrigger data-testid="filter-specialist" className="rounded-sm w-[220px] h-10"><SelectValue placeholder="Specialist" /></SelectTrigger>
            <SelectContent className="bg-card">
              <SelectItem value="all">All Specialists</SelectItem>
              <SelectItem value="__unassigned__">Unassigned</SelectItem>
              {specialistOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {bucket === "local" && statusCounts.length > 0 && (
        <div data-testid="status-counter" className="flex flex-wrap gap-2 mb-4">
          {statusCounts.map((sc) => {
            const active = status === sc.name;
            const dot = statusStyle(sc.name).dot;
            return (
              <button
                key={sc.name}
                data-testid={`status-counter-${sc.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                onClick={() => setStatus(active ? "all" : sc.name)}
                className={`flex items-center gap-2 rounded-sm border px-3 py-1.5 text-sm transition-colors ${
                  active ? "border-black bg-secondary" : "border-border bg-card hover:bg-secondary/50"
                }`}
                title={`${sc.value} in ${sc.name}${active ? " — click to clear filter" : " — click to filter"}`}
              >
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: dot }} />
                <span className="text-muted-foreground">{sc.name}</span>
                <span className="font-head font-bold tabular-nums">{sc.value.toLocaleString()}</span>
              </button>
            );
          })}
        </div>
      )}

      {canBulk && selected.size > 0 && (
        <div data-testid="bulk-action-bar" className="flex flex-wrap items-center gap-3 mb-4 p-3 border border-black/20 bg-secondary/60 rounded-sm">
          <span className="text-sm font-semibold px-1" data-testid="bulk-selected-count">{selected.size} selected</span>
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Status</span>
            <Select value={bulkStatus} onValueChange={setBulkStatus}>
              <SelectTrigger data-testid="bulk-status" className="rounded-sm w-[180px] h-9 bg-card"><SelectValue placeholder="Change status…" /></SelectTrigger>
              <SelectContent className="bg-card">
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Due Date</span>
            <Input type="date" data-testid="bulk-date" value={bulkDate}
              onChange={(e) => setBulkDate(e.target.value)} className="rounded-sm h-9 w-[160px] bg-card" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Move to</span>
            <Select value={bulkBucket} onValueChange={setBulkBucket}>
              <SelectTrigger data-testid="bulk-bucket" className="rounded-sm w-[180px] h-9 bg-card"><SelectValue placeholder="Move to board…" /></SelectTrigger>
              <SelectContent className="bg-card">
                {BUCKETS.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="bulk-apply" onClick={applyBulk} disabled={applying}
            className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 h-9">
            {applying ? "Applying…" : "Apply"}
          </Button>
          <Button variant="ghost" size="sm" data-testid="bulk-clear" onClick={clearSelection}
            className="rounded-sm h-9 text-muted-foreground">
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
        </div>
      )}

      <div className="border border-border bg-card rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/60 hover:bg-secondary/60">
              <TableHead className="w-10">
                {canBulk && (
                  <Checkbox data-testid="select-all" checked={allPageSelected} onCheckedChange={togglePage}
                    aria-label="Select all on page" />
                )}
              </TableHead>
              {["Case", "Grievant", "Discipline", "Subject", ...(showRep ? ["Rep"] : [])].map((h) => (
                <TableHead key={h} className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">{h}</TableHead>
              ))}
              {isSpecialistBucket && (
                <TableHead className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold p-0">
                  <button
                    data-testid="sort-specialist"
                    onClick={toggleSpecialistSort}
                    className="flex items-center gap-1.5 px-4 py-3 w-full hover:text-foreground transition-colors uppercase tracking-[0.15em]"
                    title="Sort by District Rep / Specialist"
                  >
                    Specialist
                    {specialistSort === "asc" ? <ArrowUp className="w-3.5 h-3.5" />
                      : specialistSort === "desc" ? <ArrowDown className="w-3.5 h-3.5" />
                      : <ArrowUpDown className="w-3.5 h-3.5 opacity-50" />}
                  </button>
                </TableHead>
              )}
              <TableHead className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold p-0">
                <button
                  data-testid="sort-date-due"
                  onClick={toggleDateSort}
                  className="flex items-center gap-1.5 px-4 py-3 w-full hover:text-foreground transition-colors uppercase tracking-[0.15em]"
                  title="Sort by date due"
                >
                  Date Due
                  {dateSort === "asc" ? <ArrowUp className="w-3.5 h-3.5" />
                    : dateSort === "desc" ? <ArrowDown className="w-3.5 h-3.5" />
                    : <ArrowUpDown className="w-3.5 h-3.5 opacity-50" />}
                </button>
              </TableHead>
              <TableHead className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold p-0">
                <button
                  data-testid="sort-status"
                  onClick={toggleStatusSort}
                  className="flex items-center gap-1.5 px-4 py-3 w-full hover:text-foreground transition-colors uppercase tracking-[0.15em]"
                  title="Sort by process order"
                >
                  Status
                  {statusSort === "asc" ? <ArrowUp className="w-3.5 h-3.5" />
                    : statusSort === "desc" ? <ArrowDown className="w-3.5 h-3.5" />
                    : <ArrowUpDown className="w-3.5 h-3.5 opacity-50" />}
                </button>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.length === 0 && (
              <TableRow><TableCell colSpan={5 + (showRep ? 1 : 0) + (isSpecialistBucket ? 1 : 0) + 2} className="text-center py-10 text-muted-foreground text-sm">No grievances found.</TableCell></TableRow>
            )}
            {paged.map((g) => (
              <TableRow key={g.id} data-testid={`grievance-row-${g.case_no}`}
                onClick={() => navigate(`/grievances/${g.id}`)}
                className={`cursor-pointer border-border ${selected.has(g.id) ? "bg-secondary/40" : ""}`}>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Checkbox data-testid={`select-${g.case_no}`} checked={selected.has(g.id)}
                    onCheckedChange={() => toggleRow(g.id)} aria-label={`Select ${g.case_no}`} />
                </TableCell>
                <TableCell className="font-mono text-xs font-medium">{g.case_no}</TableCell>
                <TableCell className="text-sm font-medium">
                  <button
                    data-testid={`grievant-link-${g.case_no}`}
                    onClick={(e) => { e.stopPropagation(); navigate(`/grievants/${encodeURIComponent(g.grievant_name)}?division=${division}${g.eid ? `&eid=${encodeURIComponent(g.eid)}` : ""}`); }}
                    className="text-left hover:underline underline-offset-2 decoration-dotted hover:text-black transition-colors"
                  >
                    {g.grievant_name}
                  </button>
                </TableCell>
                <TableCell><DisciplineTag discipline={g.discipline} /></TableCell>
                <TableCell className="text-sm max-w-[220px] truncate">{g.subject}</TableCell>
                {showRep && <TableCell className="text-sm text-muted-foreground">{g.rep || "—"}</TableCell>}
                {isSpecialistBucket && (
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap" data-testid={`specialist-${g.case_no}`}>
                    {g.specialist || "—"}
                  </TableCell>
                )}
                <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{fmtDate(g.date_due)}</TableCell>
                <TableCell><StatusBadge status={g.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between mt-3">
        <p className="text-xs text-muted-foreground">{rows.length.toLocaleString()} case(s)</p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-sm h-8" data-testid="prev-page"
            disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-xs text-muted-foreground tabular-nums">Page {page} / {totalPages}</span>
          <Button variant="outline" size="sm" className="rounded-sm h-8" data-testid="next-page"
            disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <GrievanceForm open={formOpen} onOpenChange={setFormOpen} onSaved={load} division={division} bucket={bucket} />
    </div>
  );
}
